import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "./cn";

/**
 * Variantes da etiqueta, portadas do `badge.tsx` do CRM.
 *
 * Diferença deliberada: o CRM usa `bg-green-100`/`bg-yellow-100` (cores da paleta
 * do Tailwind) nas variantes de sucesso e atenção. Aqui elas usam os tokens
 * `success` e `warning`, então o modo escuro funciona sem uma segunda regra — com
 * `green-100` fixo, a etiqueta fica clara demais sobre fundo escuro.
 */
export const variantesDaEtiqueta = cva(
  [
    "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5",
    "text-xs font-medium transition-colors",
    "[&_svg]:size-3 [&_svg]:shrink-0",
  ],
  {
    variants: {
      tom: {
        neutro: "border-transparent bg-muted text-muted-foreground",
        primario: "border-transparent bg-primary text-primary-foreground",
        secundario: "border-transparent bg-secondary text-secondary-foreground",
        contorno: "border-border text-foreground",
        sucesso: "border-transparent bg-success/12 text-success",
        atencao: "border-transparent bg-warning/15 text-warning-foreground",
        perigo: "border-transparent bg-destructive/12 text-destructive",
      },
    },
    defaultVariants: { tom: "neutro" },
  },
);

export type TomDaEtiqueta = NonNullable<
  VariantProps<typeof variantesDaEtiqueta>["tom"]
>;

export type PropsDaEtiqueta = Omit<HTMLAttributes<HTMLSpanElement>, "className"> &
  VariantProps<typeof variantesDaEtiqueta> & {
    readonly className?: string | undefined;
  };

/**
 * Etiqueta de status.
 *
 * O texto carrega o significado; a cor apenas reforça. Etiqueta que comunica só
 * por cor é ilegível para quem não distingue as duas — e some por completo no
 * modo de alto contraste.
 */
export function Etiqueta({ tom, className, ...resto }: PropsDaEtiqueta) {
  return (
    <span {...resto} className={cn(variantesDaEtiqueta({ tom }), className)} />
  );
}
